import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box, Typography, Button, AppBar, Toolbar } from '@mui/material'
import { InvoiceForm } from './components/invoice'
import { useAuth } from './components/auth/AuthProvider'
import { salesService } from './services/api'
import HomePage from './pages/HomePage'

const handleSaveInvoice = async (form: any) => {
  try {
    // Map UI form to backend Sales API payload
    const def_branch = Number(form.branch_id || form.def_branch || 1)
    const staff_user_id = Number(form.staff_user_id || 1)
    const bond_date = form.invoice_date || new Date().toISOString().slice(0, 10)

    // Map line items to "val" entries
    const val = (form.line_items || []).map((li: any) => ({
      visible_product_code: li.product_code,
      product_code: String(li.product_code || ''),
      karat: li.carat ?? '',
      quantity_num: li.quantity ?? '1',
      insertion_num: li.serial_number ?? '',
      carat: li.carat ?? '',
      unit: li.unit ?? '',
      quantity: String(li.weight ?? li.quantity ?? 0),
      stone_weight: String(li.stone_weight ?? ''),
      cost_center_id_1: '-1',
      selling_price: String(li.unit_price ?? ''),
      salary: String(li.salary ?? ''),
      total_salaries: String(li.total_salary ?? ''),
      discount: String(li.discount ?? '0'),
      discount_value: '',
      vat: String(li.tax_rate ?? '15'),
      total: String(li.total ?? ''),
      client_name: form.customer_name ?? '',
      bond_num: form.document_number ?? '',
      selling_time: form.timing ?? '',
      bond_date,
      payment_type: String(form.payment_type ?? '1'),
      parent_account_guide_id: String(form.parent_account_guide_id ?? form.customer_id ?? ''),
      account_guide_id: String(form.parent_account_guide_id ?? form.customer_id ?? ''),
      sales_state: form.statement ?? '',
      payment_method_sales: String(form.payment_method_sales ?? '1'),
      payment_method: String(form.payment_method ?? '1'),
      def_branch: String(def_branch),
      // first payment shortcut (optional)
      payment_1_amount: form.payment_methods?.[0]?.amount ? String(form.payment_methods[0].amount) : undefined,
      payment_1_payment_method: form.payment_methods?.[0]?.method ?? undefined,
      payment_1_reference: form.payment_methods?.[0]?.reference ?? undefined,
      payment_1_currency_id: form.payment_methods?.[0]?.currency_id ?? undefined,
      payment_1_account_guide_id: form.payment_methods?.[0]?.account_id ?? undefined,
    }))

    const payload = {
      def_branch,
      staff_user_id,
      payment_type: Number(form.payment_type ?? 1) as 1 | 2,
      payment_method: String(form.payment_method ?? '1'),
      client_name: form.customer_name ?? '',
      parent_account_guide_id: form.parent_account_guide_id || form.customer_id || undefined,
      customer_phone: form.customer_phone ?? '',
      sales_state: form.statement ?? '',
      delivery_address_id: form.delivery_address_id ?? undefined,
      delivery_address: form.delivery_address ?? undefined,
      invoice_address_id: form.invoice_address_id ?? undefined,
      invoice_address: form.invoice_address ?? undefined,
      client_type: form.client_type ?? undefined,
      identity_type: form.identity_type ?? undefined,
      tax_or_identity_number: form.tax_number ?? undefined,
      print_without_header: '0' as const,
      print_grouped_items: '0' as const,
      print_with_background: '0' as const,
      val,
    }

    const res = await salesService.create(payload)
    if (res.success) {
      alert('تم حفظ الفاتورة بنجاح!')
      if (res.data?.printUrl) {
        window.open(res.data.printUrl, '_blank')
      }
    } else {
      alert(res.message || 'فشل حفظ الفاتورة')
    }
  } catch (e: any) {
    alert(e?.message || 'حدث خطأ أثناء حفظ الفاتورة')
  }
}

const UserInfoBar: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <AppBar position="static" sx={{ mb: 2 }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          مرحباً، {user?.name} ({user?.user_code})
        </Typography>
        <Button color="inherit" onClick={logout}>
          تسجيل الخروج
        </Button>
      </Toolbar>
    </AppBar>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Home page route */}
      <Route path="/" element={<HomePage />} />
      
      {/* Main invoice creation route */}
      <Route 
        path="/invoice/create" 
        element={
          <Box>
            <UserInfoBar />
            <InvoiceForm
              onSave={handleSaveInvoice}
              onCancel={() => {
                // Redirect back to home page
                window.location.href = '/'
              }}
            />
          </Box>
        } 
      />
      
      {/* Invoice creation with pre-filled data (for editing) */}
      <Route 
        path="/invoice/create/:invoiceId" 
        element={
          <Box>
            <UserInfoBar />
            <InvoiceForm
              onSave={handleSaveInvoice}
              onCancel={() => {
                // Redirect back to home page
                window.location.href = '/'
              }}
            />
          </Box>
        } 
      />
      
      {/* Placeholder routes for other invoice types */}
      <Route path="/sales-discount/create" element={<Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h4">إشعار خصم مبيعات - قريباً</Typography></Box>} />
      <Route path="/purchase/create" element={<Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h4">فاتورة مشتريات - قريباً</Typography></Box>} />
      <Route path="/purchase-return/create" element={<Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h4">إرجاع مشتريات - قريباً</Typography></Box>} />
      <Route path="/receipt/create" element={<Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h4">سند قبض - قريباً</Typography></Box>} />
      <Route path="/payment/create" element={<Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h4">سند صرف - قريباً</Typography></Box>} />
      
      {/* Catch all other routes and redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default AppRoutes 